"use client"

import * as React from "react"
import { CircleCheckIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Trash2, ExternalLink } from "lucide-react"
import { NotificationItem } from "@/types/notification"
import { AppIcon } from "@/components/desk/AppIcon"

interface NotificationItemCardProps {
  item: NotificationItem
  onDelete?: (id: number) => void
}

export function NotificationItemCard({ item, onDelete }: NotificationItemCardProps) {
  const variant = (item.type || "info").toLowerCase()

  // Cores do cartão por tipo semântico de notificação
  const getVariantStyles = () => {
    switch (variant) {
      case "success":
        return {
          card: "bg-emerald-50 dark:bg-emerald-950/50 border-emerald-200 dark:border-emerald-900/60 text-emerald-950 dark:text-emerald-100",
          typeIcon: <CircleCheckIcon className="size-3 text-emerald-600 dark:text-emerald-400" />
        }
      case "warning":
        return {
          card: "bg-amber-50 dark:bg-amber-950/50 border-amber-200 dark:border-amber-900/60 text-amber-950 dark:text-amber-100",
          typeIcon: <TriangleAlertIcon className="size-3 text-amber-600 dark:text-amber-400" />
        }
      case "error":
        return {
          card: "bg-red-50 dark:bg-red-950/50 border-red-200 dark:border-red-900/60 text-red-950 dark:text-red-100",
          typeIcon: <OctagonXIcon className="size-3 text-red-600 dark:text-red-400" />
        }
      case "info":
      default:
        return {
          card: "bg-blue-50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-900/60 text-blue-950 dark:text-blue-100",
          typeIcon: <InfoIcon className="size-3 text-blue-600 dark:text-blue-400" />
        }
    }
  }

  const styles = getVariantStyles()
  const appLogoUrl = item.avatarUrl || item.appIcon
  const appName = item.appName || "App"

  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border shadow-xs hover:shadow-md transition-all duration-300 ease-in-out group relative font-sans ${styles.card}`}>
      {/* Ícone do Aplicativo no padrão oficial do Desk (usando AppIcon) */}
      <div className="relative shrink-0 mt-0.5">
        <AppIcon
          iconUrl={appLogoUrl && !appLogoUrl.includes("cdn.boldworkspace.com.br") ? appLogoUrl : undefined}
          name={appName}
          size="medium"
        />

        {/* Badge do Tipo Semântico no cantinho da Logo do App */}
        <div className="absolute -bottom-1 -right-1 bg-white dark:bg-zinc-900 p-0.5 rounded-full shadow-xs border border-zinc-200 dark:border-zinc-800">
          {styles.typeIcon}
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex-1 min-w-0 pr-4">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-xs font-bold truncate">
            {item.title}
          </span>
          <span className="text-[10px] opacity-70 shrink-0 font-sans">{item.time}</span>
        </div>

        <p className="text-xs opacity-90 leading-relaxed font-sans line-clamp-2">
          {item.text}
        </p>

        {/* Botão Call to Action (CTA) se enviado pelo app com suporte a direcionamento (same_tab, new_tab, app_tab) */}
        {item.action && (
          <div className="mt-2 pt-1 border-t border-black/5 dark:border-white/5 flex justify-end">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (!item.action) return
                const targetMode = (item.action.target || 'app_tab').toLowerCase()
                const url = item.action.url

                if (targetMode === 'same_tab' || targetMode === '_self' || targetMode === 'self') {
                  if (url) window.location.href = url
                } else if (targetMode === 'new_tab' || targetMode === '_blank' || targetMode === 'blank') {
                  if (url) window.open(url, '_blank')
                } else {
                  // 'app_tab' / 'app': Ativa/Redireciona iframe do app no Bold Workspace sem atualizar a página principal
                  const appId = item.action.appId || item.appId
                  if (url) {
                    const iframe = (
                      (appId ? document.querySelector(`iframe[data-instance-id="${appId}"], iframe[data-app-id="${appId}"]`) : null) ||
                      document.querySelector(`iframe`)
                    ) as HTMLIFrameElement | null

                    if (iframe) {
                      if (iframe.contentWindow) {
                        try {
                          iframe.contentWindow.postMessage({ type: 'BOLD_NAVIGATE', payload: { path: url } }, '*')
                        } catch (err) {}
                      }
                      if (iframe.src !== url) {
                        iframe.src = url
                      }
                    }
                  }
                }
              }}
              className="flex items-center gap-1.5 bg-[#004EB1] hover:bg-[#003d8d] text-white text-[10px] font-bold px-2.5 py-1 rounded-md shadow-xs transition-colors cursor-pointer"
            >
              <span>{item.action.label}</span>
              <ExternalLink className="size-3" />
            </button>
          </div>
        )}
      </div>

      {/* Botão de Excluir da Central */}
      {onDelete && (
        <button
          type="button"
          onClick={() => onDelete(item.id)}
          className="absolute right-2 top-2 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10 dark:hover:bg-white/10 cursor-pointer"
          title="Excluir notificação"
        >
          <Trash2 className="size-3.5 opacity-60 hover:opacity-100" />
        </button>
      )}
    </div>
  )
}
